import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { usePagedFetch } from "../api/usePagedFetch";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { AUDIT_DETECTOR_PROTOCOL, AUDIT_DETECTOR_TAB, AUDIT_TAB_TOOLTIP, AUDIT_TABS_BY_PROTOCOL, AUDIT_VERDICT_ORDER, auditVerdictInfo, detectorLabel, FIRST_CALCULATION_TEXT, GENUINE_DISAGREEMENT_FIELDS, reachabilityConfidenceInfo, reachabilityPositionNote, REGIONS_TAB_NOTE, type AuditTabId, type AuditVerdict, type AuditWindow } from '../lib/definitions/analysis';
import { AUDIT_SETTINGS_GROUPS, type AuditSettingsGroupId } from '../lib/definitions/settings';
import { BACKBONE_TAB_INTRO, BACKBONE_DEFAULT_WINDOW } from '../lib/definitions/topology';
import { colorFor } from '../lib/definitions/palette';
import { isProtocol, PROTOCOL_LABEL, type Protocol } from '../lib/definitions/protocols';
import { PAGE_SIZE } from '../lib/definitions/navigation';
import { formatNumber } from "../lib/format";
import { formatDistanceMiles } from "../lib/units";
import { ObserverBadge } from "../components/ObserverBadge";
import { PagingBar } from "../components/PagingBar";
import { RfHealthTab } from "../components/RfHealthTab";
import { LocationCheckTab } from "../components/LocationCheckTab";
import { PathsTab } from "../components/PathsTab";
import { PathInspectorTab } from "../components/PathInspectorTab";
import { TalkersTab } from "../components/TalkersTab";
import { TopologyTab } from "../components/TopologyTab";
import { NeighbourGraphTab } from "../components/NeighbourGraphTab";
import { DistanceTab } from "../components/DistanceTab";
import { SignalTab } from "../components/SignalTab";
import { BehaviourTab } from "../components/BehaviourTab";
import { AuditTable } from "../components/AuditTable";
import { AuditHistoryProvider, AuditWindowControls, useAuditHistory } from "../components/AuditHistory";
import { effectiveWindow } from "../lib/auditHistory";
import type {
  AuditDetector,
  AuditFinding,
  AuditFindingsResponse,
  CoverageDisagreement,
  CoverageDisagreementsResponse,
  ReachabilityResponse,
} from "../api/types";

// Protocol split (2026-09-25): a top-level MeshCore | Meshtastic switch
// above the tabs, each side with its own tab set (lib/definitions.ts's
// AUDIT_TABS_BY_PROTOCOL) — replaces the old single, protocol-mixed
// AUDIT_SUBMENUS list (byte_hash/behaviour/reachability/regions/
// meshcore_config). "Byte hash" is retired as its own tab: D4/D5, both
// MeshCore-only, stay reachable as ordinary entries in the Behaviour
// tab's own Check filter instead. "MeshCore config" is renamed "Config"
// (MeshCoreConfigAudit.tsx was already MeshCore-only). "Regions" has no
// detector — see RegionsSubmenuPanel below — and renders as a tab on
// BOTH sides, same component either way.
//
// The three-verdict rule (lib/definitions.ts's AUDIT_VERDICT_INFO) is
// enforced here by construction, same as before: there is no fourth
// branch anywhere below that could render "clean"/"healthy", and an empty
// findings list always reads as "Nothing detected in this window" — never
// "all good."
//
// "Disagreements" (do two observers report conflicting
// facts about the same node) lives on the Reachability tab, filtered down to fields
// worth showing — see GENUINE_DISAGREEMENT_FIELDS in lib/definitions.ts
// and GenuineDisagreementsSection below for why and how.
function computeInitialProtocol(presetDetector: string | null | undefined, presetProtocol: string | null | undefined): Protocol {
  if (isProtocol(presetProtocol)) return presetProtocol;
  if (presetDetector) {
    const forced = AUDIT_DETECTOR_PROTOCOL[presetDetector];
    if (forced) return forced;
  }
  return "meshcore";
}

function computeInitialTab(
  presetDetector: string | null | undefined,
  presetSubmenu: string | null | undefined,
  protocol: Protocol,
): AuditTabId {
  const tabs = AUDIT_TABS_BY_PROTOCOL[protocol];
  if (presetSubmenu && tabs.some((t) => t.id === presetSubmenu)) return presetSubmenu as AuditTabId;
  if (presetDetector) {
    const tab = AUDIT_DETECTOR_TAB[presetDetector];
    if (tab && tabs.some((t) => t.id === tab)) return tab;
  }
  return tabs[0].id;
}

// Writes `protocol` into the current `#/audit?...` hash query string
// without touching any other key — same "own exactly one key, leave the
// rest alone" discipline BehaviourTab.tsx's own writeFilterState uses for
// its own keys (sub/window/view/level/...). This is the one key the two
// components share: BehaviourTab reads it back out of the same hash at
// its own mount (its FilterState.protocol) and is kept in sync with the
// live `protocol` prop afterward — see that file's own sync effect.
function writeProtocolToHash(protocol: Protocol): void {
  const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const qIdx = raw.indexOf("?");
  const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const params = new URLSearchParams(qIdx >= 0 ? raw.slice(qIdx + 1) : "");
  params.set("protocol", protocol);
  const newHash = `#${pathPart}?${params.toString()}`;
  if (newHash !== window.location.hash) window.history.replaceState(null, "", newHash);
}

function RegionsSubmenuPanel() {
  // Deliberately not wired to any endpoint. The region MODEL itself is no
  // longer in flux — the bbox contract landed 2026-09-20 (operator-defined
  // regions, optionally starting from meshmapper.net, min_lat/max_lat/
  // min_lon/max_lon; see lib/regions.ts and Alerts > Regions) — but that only means region
  // GEOMETRY is settled, not that any audit finding is region-scoped yet.
  // No detector or endpoint on this backend currently attaches a region to
  // a finding, so there is nothing real to fetch here. This submenu exists
  // so "regions" isn't silently missing from the audit surface, but its
  // content stays an honest pending note rather than a fetch against a
  // shape that doesn't exist, until a detector actually produces
  // region-scoped findings.
  // 2026-09-30 layout task: the tab-level "Regions" heading (repeated the
  // sub-tab name) is gone. 2026-09-30 follow-up: the note itself is BACK
  // as visible body content — an empty tab pane read as broken, and this
  // note is the tab's entire content, not an intro to something else
  // below it — while also staying as the sub-tab button's own hover
  // tooltip (AUDIT_TAB_TOOLTIP.regions). Same REGIONS_TAB_NOTE string
  // both places.
  return (
    <section className="page-section">
      <div className="node-detail-note">{REGIONS_TAB_NOTE}</div>
    </section>
  );
}

// Spec for this table: node seen by # of observers for meshcore and # of
// feeders for meshtastic, plus a furthest-from-origin figure for each one —
// hop count and distance in miles from GPS coordinates, as long as origin
// is within the standard mesh (some are wrong and end up at GPS 0,0; those
// should be ignored as valid locations). Reachability is marked proven or
// questionable: hitting the same reach over and over is proven, but a
// single hit is probably tropospheric ducting.
//
// GET /api/audit/reachability landed live 2026-09-20 (the coordinator: an
// earlier round was told not to build this fetch because the endpoint
// didn't exist yet — it existed by the time of this round and is wired up
// here). Unlike several other endpoints in this file, this one has real
// server-side window/protocol/confidence filters and true cursor
// pagination — no bounded-scan workaround needed.
function ReachabilityTable({ windowKey, protocol }: { windowKey: AuditWindow; protocol: Protocol }) {
  const { effectiveTheme, displaySettings } = useAppData();
  const { takenAt } = useAuditHistory();
  const distPref = displaySettings?.display.distance_unit;
  const [confidenceFilter, setConfidenceFilter] = useState<string>("all");

  const queryParams = useMemo(() => {
    const p: Record<string, string> = { window: windowKey, protocol };
    if (takenAt) p.taken_at = takenAt;
    if (confidenceFilter !== "all") p.confidence = confidenceFilter;
    return p;
  }, [windowKey, protocol, confidenceFilter, takenAt]);

  // 2026-09-30 Stage B slice 2: GET /api/audit/reachability is snapshot-
  // only now -- a fresh install (nothing computed yet) returns `{pending:
  // true, ...}` instead of a real ReachabilityResponse; see BackboneTab.
  // tsx's own identical `"pending" in res` pattern, wired first in slice 1.
  const { data: rawData, page, hasPrev, hasNext, next, prev } = usePagedFetch<ReachabilityResponse | { pending: true }>(
    "/api/audit/reachability", queryParams, PAGE_SIZE,
  );
  const data: ReachabilityResponse | null | "unavailable" | "pending" =
    rawData && rawData !== "unavailable" && "pending" in rawData && rawData.pending
      ? "pending"
      : (rawData as ReachabilityResponse | null | "unavailable");

  const nodes = data && data !== "unavailable" && data !== "pending" ? data.nodes : [];
  const totalCount = data && data !== "unavailable" && data !== "pending" ? (data.total ?? nodes.length) : 0;
  const rangeStart = nodes.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + nodes.length - 1;

  function goToNode(nativeId: string) {
    navigate(`#/nodes/${encodeURIComponent(nativeId)}`);
  }

  return (
    <section className="page-section">
      {/* 2026-09-30 layout task: the tab-level "Reachability" heading
          (repeated the sub-tab name) and this intro note are gone — the
          note's own text now lives as a hover tooltip on the Reachability
          sub-tab button (AUDIT_TAB_TOOLTIP.reachability). */}
      <div className="node-list-toolbar">
        <div className="theme-toggle" role="group" aria-label="Confidence">
          <button data-active={confidenceFilter === "all"} onClick={() => setConfidenceFilter("all")}>
            All confidence
          </button>
          <button data-active={confidenceFilter === "proven"} onClick={() => setConfidenceFilter("proven")}>
            {reachabilityConfidenceInfo("proven").label}
          </button>
          <button data-active={confidenceFilter === "questionable"} onClick={() => setConfidenceFilter("questionable")}>
            {reachabilityConfidenceInfo("questionable").label}
          </button>
        </div>
      </div>

      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "pending" && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {data === "unavailable" && <div className="node-detail-note">Reachability audit data didn't respond as expected.</div>}

      {data && data !== "unavailable" && data !== "pending" && (
        <div className="node-list-table-wrap">
          <table className="node-list-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Heard by</th>
                <th>Max hops</th>
                <th>Furthest reach</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n) => {
                const protoLabel = isProtocol(n.node.protocol) ? PROTOCOL_LABEL[n.node.protocol] : n.node.protocol;
                const badReason = reachabilityPositionNote(n.node.position_status, n.node.position_note);
                return (
                  <tr key={n.node.native_id} className="node-list-row" onClick={() => goToNode(n.node.native_id)}>
                    <td>
                      {n.node.label || n.node.native_id} <span className="audit-subject-protocol">({protoLabel})</span>
                    </td>
                    <td>
                      <div>
                        {/* heard_by_kind is already plural on the wire
                            ("observers"/"feeders") — singularised only for
                            count===1, same grammar
                            handling routeProvenanceLabel already uses for
                            "hop(s)", never a different word. */}
                        <strong>{formatNumber(n.heard_by_count)}</strong> {n.heard_by_count === 1 ? n.heard_by_kind.replace(/s$/, "") : n.heard_by_kind}
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                        {n.heard_by.map((oid) => (
                          <ObserverBadge key={oid} observerId={oid} compact />
                        ))}
                      </div>
                    </td>
                    <td>{n.max_hops ?? "--"}</td>
                    <td>
                      {n.furthest ? (
                        <>
                          <span
                            className="badge"
                            style={{ background: colorFor(reachabilityConfidenceInfo(n.furthest.confidence).color, effectiveTheme) }}
                          >
                            {reachabilityConfidenceInfo(n.furthest.confidence).label}
                          </span>
                          <div>
                            {formatDistanceMiles(n.furthest.miles, distPref)} over {n.furthest.hops} hop(s)
                          </div>
                          <div className="settings-field-desc">
                            via {n.furthest.receiver.label || n.furthest.receiver.native_id} · {formatNumber(n.furthest.observations)}{" "}
                            observation(s)
                          </div>
                        </>
                      ) : (
                        // furthest: null means no computable distance, not zero —
                        // say why (a bad position) or, when the position is
                        // perfectly valid and there's simply not enough data yet,
                        // say that instead of implying a position problem that
                        // isn't real.
                        <span className="settings-field-desc">{badReason ?? "No computable distance yet — not enough observations."}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {nodes.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty-state">
                    No nodes in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && data !== "unavailable" && data !== "pending" && nodes.length > 0 && (
        <PagingBar rangeStart={rangeStart} rangeEnd={rangeEnd} total={totalCount} hasPrev={hasPrev} hasNext={hasNext} onPrev={prev} onNext={next} />
      )}
    </section>
  );
}

// Observer disagreements, moved here from the retired Coverage destination.
// Only fields worth calling a real disagreement are shown — GENUINE_DISAGREEMENT_FIELDS
// in lib/definitions.ts (position/role/name) — since
// /api/coverage/disagreements has no server-side field filter (confirmed
// via OpenAPI), this scans every page of the window (bounded, capped) and
// filters client-side rather than paging the raw, mostly-noise stream:
// with ~95% of raw entries being last_heard timing noise, paging the raw
// stream would show near-empty pages with a misleading "hasNext" and a
// total that counts noise as if it were signal — the same hollow-page
// problem the Hazards reason-code breakdown was built to avoid. The
// filtered, resident set is small (a few dozen at most, confirmed live)
// so client-side paging over it is the honest choice, not a shortcut.
const DISAGREEMENT_SCAN_PAGE_LIMIT = 500;
const DISAGREEMENT_SCAN_MAX_PAGES = 8; // 8 * 500 = 4,000 raw entries of headroom

interface DisagreementScanState {
  loading: boolean;
  genuine: CoverageDisagreement[];
  rawScanned: number;
  truncated: boolean;
  // 2026-09-30 Stage B slice 2: /api/coverage/disagreements is snapshot-
  // only now -- a fresh install's first page comes back `{pending: true,
  // ...}` instead of a real CoverageDisagreementsResponse. The scan stops
  // at the first page either way (there is nothing later to page through
  // yet), same as it already stops on a genuine fetch failure (`!res`).
  pending: boolean;
}

function useGenuineDisagreements(windowKey: AuditWindow, protocol: Protocol): DisagreementScanState {
  const { takenAt } = useAuditHistory();
  const paramsKey = JSON.stringify({ windowKey, protocol, takenAt });
  const [state, setState] = useState<DisagreementScanState>({ loading: true, genuine: [], rawScanned: 0, truncated: false, pending: false });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, genuine: [], rawScanned: 0, truncated: false, pending: false });
    (async () => {
      const genuine: CoverageDisagreement[] = [];
      let cursor: string | null = null;
      let rawScanned = 0;
      let truncated = false;
      for (let page = 0; page < DISAGREEMENT_SCAN_MAX_PAGES; page++) {
        const search = new URLSearchParams({ window: windowKey, protocol });
        if (takenAt) search.set("taken_at", takenAt);
        search.set("limit", String(DISAGREEMENT_SCAN_PAGE_LIMIT));
        if (cursor) search.set("cursor", cursor);
        const res = await tryApiGet<CoverageDisagreementsResponse | { pending: true }>(`/api/coverage/disagreements?${search.toString()}`);
        if (cancelled) return;
        if (!res) {
          truncated = true;
          break;
        }
        if ("pending" in res && res.pending) {
          if (!cancelled) setState({ loading: false, genuine: [], rawScanned: 0, truncated: false, pending: true });
          return;
        }
        const page_ = res as CoverageDisagreementsResponse;
        for (const item of page_.items) {
          if (GENUINE_DISAGREEMENT_FIELDS.includes(item.field)) genuine.push(item);
        }
        rawScanned += page_.items.length;
        if (!page_.next_cursor) break;
        cursor = page_.next_cursor;
        if (page === DISAGREEMENT_SCAN_MAX_PAGES - 1) truncated = true;
      }
      if (!cancelled) setState({ loading: false, genuine, rawScanned, truncated, pending: false });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return state;
}

function disagreementSubjectLabel(s: CoverageDisagreement["subject"]): string {
  return s.label || s.native_id;
}

function GenuineDisagreementsSection({ windowKey, protocol }: { windowKey: AuditWindow; protocol: Protocol }) {
  const { observerTrust } = useAppData();
  const scan = useGenuineDisagreements(windowKey, protocol);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [scan.genuine]);

  const pageItems = scan.genuine.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const rangeStart = pageItems.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + pageItems.length - 1;
  const excludedCount = scan.rawScanned - scan.genuine.length;

  function goToSubject(s: CoverageDisagreement["subject"]) {
    navigate(`#/nodes/${encodeURIComponent(s.native_id)}`);
  }

  return (
    <section className="page-section">
      <div className="page-section-title">Observer disagreements</div>
      <div className="node-detail-note">
        Only genuine conflicts are shown — position, role, or name differing between observers. Timing differences
        (last_heard) are polling latency, not a real disagreement, and are excluded rather than shown as one.
        {!scan.loading && excludedCount > 0 && ` ${formatNumber(excludedCount)} of ${formatNumber(scan.rawScanned)} raw entries excluded on that basis.`}
      </div>

      {scan.loading && <div className="node-detail-note">Scanning the window for genuine disagreements…</div>}
      {!scan.loading && scan.pending && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}

      {!scan.loading && !scan.pending && (
        <div className="node-list-table-wrap">
          <table className="node-list-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Field</th>
                <th>Values</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((d, i) => (
                <tr key={`${d.subject.protocol}-${d.subject.native_id}-${d.field}-${i}`} className="node-list-row" onClick={() => goToSubject(d.subject)}>
                  <td>
                    {disagreementSubjectLabel(d.subject)}{" "}
                    <span className="audit-subject-protocol">({isProtocol(d.subject.protocol) ? PROTOCOL_LABEL[d.subject.protocol] : d.subject.protocol})</span>
                  </td>
                  <td>{d.field}</td>
                  <td>
                    <ul className="coverage-disagreement-values">
                      {d.values.map((v, vi) => (
                        <li key={vi}>
                          <ObserverBadge observerId={v.observer_id} trustTier={observerTrust.get(v.observer_id)} />
                          <span>{typeof v.value === "object" ? JSON.stringify(v.value) : String(v.value)}</span>
                          <span className="coverage-disagreement-ts">{new Date(v.observed_at).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={3} className="empty-state">
                    No genuine disagreements (position, role, or name conflicts) in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!scan.loading && pageItems.length > 0 && (
        <PagingBar
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          total={scan.genuine.length}
          hasPrev={page > 0}
          hasNext={(page + 1) * PAGE_SIZE < scan.genuine.length}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
          note={scan.truncated ? `Scanned the first ${formatNumber(scan.rawScanned)} raw entries in this window — there may be more.` : undefined}
        />
      )}
    </section>
  );
}

export function AuditPage(props: {
  presetDetector?: string | null;
  presetSubmenu?: string | null;
  presetProtocol?: string | null;
}) {
  return (
    <AuditHistoryProvider>
      <AuditPageBody {...props} />
    </AuditHistoryProvider>
  );
}

function AuditPageBody({
  presetDetector,
  presetSubmenu,
  presetProtocol,
}: {
  presetDetector?: string | null;
  presetSubmenu?: string | null;
  presetProtocol?: string | null;
}) {
  const [detectors, setDetectors] = useState<AuditDetector[] | "unavailable" | null>(null);
  const [protocol, setProtocol] = useState<Protocol>(() => computeInitialProtocol(presetDetector, presetProtocol));
  const [tab, setTab] = useState<AuditTabId>(() => computeInitialTab(presetDetector, presetSubmenu, protocol));
  const [highlightDetector, setHighlightDetector] = useState<string | null>(presetDetector ?? null);
  const [windowKey, setWindowKey] = useState<AuditWindow>("24h");
  // One window shared by Topology, Distance and Signal.
  const [backboneWindow, setBackboneWindow] = useState<AuditWindow>(BACKBONE_DEFAULT_WINDOW);
  // While a History snapshot is chosen every tab shows its 7-day result.
  const { takenAt } = useAuditHistory();
  const shownReachWindow = effectiveWindow(windowKey, takenAt);
  const shownBackboneWindow = effectiveWindow(backboneWindow, takenAt);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<{ detectors: AuditDetector[] }>("/api/audit/detectors")
      .then((res) => !cancelled && setDetectors(res?.detectors ?? "unavailable"))
      .catch(() => !cancelled && setDetectors("unavailable"));
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-derive on every navigation, not just first mount — an Ask citation
  // clicked while already on the Audit page (e.g. a different detector's
  // finding) must still land on the right protocol side and tab, the same
  // guarantee the old single-table page gave via presetDetector alone.
  useEffect(() => {
    const nextProtocol = computeInitialProtocol(presetDetector, presetProtocol);
    setProtocol(nextProtocol);
    setTab(computeInitialTab(presetDetector, presetSubmenu, nextProtocol));
    setHighlightDetector(presetDetector ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetDetector, presetSubmenu, presetProtocol]);

  useEffect(() => {
    if (!highlightDetector) return;
    const el = document.querySelector(`[data-detector-id="${CSS.escape(highlightDetector)}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [tab, highlightDetector, detectors]);

  const tabs = AUDIT_TABS_BY_PROTOCOL[protocol];

  // Switching protocol keeps you on the same tab name when that tab
  // exists on the other side (Behaviour/Reachability, on both; Regions,
  // on both), else falls back to the new side's first tab (Config has no
  // Meshtastic counterpart, so MeshCore > Config > Meshtastic lands on
  // Meshtastic's own first tab, Behaviour) — task's own explicit rule.
  function selectProtocol(next: Protocol) {
    setProtocol(next);
    setHighlightDetector(null);
    const nextTabs = AUDIT_TABS_BY_PROTOCOL[next];
    setTab((current) => (nextTabs.some((t) => t.id === current) ? current : nextTabs[0].id));
    writeProtocolToHash(next);
  }

  function selectTab(id: AuditTabId) {
    setTab(id);
    setHighlightDetector(null);
  }

  return (
    <div className="node-list-page audit-page">
      {/* 2026-09-30 layout task: title and the MeshCore | Meshtastic switch
          share one row now (audit-header-row) instead of two stacked ones;
          the old "Recalculate" / "Calculated ... (N min ago)" header is
          gone entirely — the background recompute job (audit/recompute.py)
          still runs and keeps the cache warm, this page just no longer
          surfaces a manual trigger or a timestamp for it. */}
      <div className="audit-header-row">
        <h2 className="audit-page-title">Analysis</h2>
        <div className="theme-toggle" role="group" aria-label="Protocol">
          {(["meshcore", "meshtastic"] as Protocol[]).map((p) => (
            <button key={p} data-active={protocol === p} onClick={() => selectProtocol(p)}>
              {PROTOCOL_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="theme-toggle audit-tab-row" role="group" aria-label="Tab" style={{ marginBottom: 10 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            data-active={tab === t.id}
            onClick={() => selectTab(t.id)}
            title={t.id === "backbone" ? BACKBONE_TAB_INTRO : AUDIT_TAB_TOOLTIP[t.id]}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="audit-settings-links" aria-label="Related settings">
        {(() => {
          const groupByTab: Partial<Record<AuditTabId, AuditSettingsGroupId>> = {
            audit: "adverts",
            behaviour: "freshness",
            rf_health: "freshness",
            signal: "clock",
            location: "location",
            reachability: "reach",
            distance: "topology",
            backbone: "topology",
            neighbour_graph: "topology",
            regions: "freshness",
            paths: "freshness",
            path_inspector: "freshness",
            talkers: "adverts",
          };
          const relevantGroup = groupByTab[tab];
          const relevantLabel = AUDIT_SETTINGS_GROUPS.find((group) => group.id === relevantGroup)?.label;
          return (
            <>
              {relevantGroup && relevantGroup !== "freshness" && (
                <button type="button" onClick={() => navigate(`#/settings?category=audit&group=${relevantGroup}`)}>
                  Settings: {relevantLabel}
                </button>
              )}
              <button type="button" onClick={() => navigate("#/settings?category=audit&group=freshness")}>
                Settings: Analysis freshness
              </button>
            </>
          );
        })()}
      </div>

      <div>
        {tab === "reachability" && protocol === "meshtastic" && (
          <>
            <div className="node-list-toolbar">
              <AuditWindowControls windowKey={shownReachWindow} onWindowChange={setWindowKey} />
            </div>

            <ReachabilityTable windowKey={shownReachWindow} protocol={protocol} />

            <GenuineDisagreementsSection windowKey={shownReachWindow} protocol={protocol} />
          </>
        )}

        {tab === "audit" && protocol === "meshcore" && <AuditTable />}
        {tab === "behaviour" && <BehaviourTab protocol={protocol} />}
        {tab === "regions" && <RegionsSubmenuPanel />}
        {tab === "rf_health" && protocol === "meshcore" && <RfHealthTab />}
        {tab === "paths" && protocol === "meshcore" && <PathsTab />}
        {tab === "path_inspector" && protocol === "meshcore" && <PathInspectorTab />}
        {tab === "talkers" && protocol === "meshcore" && <TalkersTab />}
        {tab === "backbone" && protocol === "meshcore" && <TopologyTab windowKey={shownBackboneWindow} onWindowChange={setBackboneWindow} />}
        {tab === "neighbour_graph" && protocol === "meshcore" && <NeighbourGraphTab />}
        {tab === "distance" && protocol === "meshcore" && <DistanceTab windowKey={shownBackboneWindow} onWindowChange={setBackboneWindow} />}
        {tab === "location" && <LocationCheckTab protocol={protocol} windowKey={shownBackboneWindow} onWindowChange={setBackboneWindow} />}
        {tab === "signal" && protocol === "meshcore" && <SignalTab windowKey={shownBackboneWindow} onWindowChange={setBackboneWindow} />}
      </div>
    </div>
  );
}
